// CountyPickerModal — one-time (per onboarding) county picker for the
// Free/Scout geography-aware tier redesign. Free picks 1 county, Scout up
// to 3; full property data (assessed value, equity) is only visible inside
// the chosen counties — everywhere else the card/detail panel shows the
// existing fuzzed placeholder (see PropertyCard.tsx / PropertyDetail.tsx).
//
// Structural pattern follows UpgradePrompt.tsx ({backdrop > modal}, same
// class-naming convention, backdrop-click-to-dismiss via stopPropagation on
// the modal itself) — NOT ProductTour.tsx's anchor-based tour, since
// there's no DOM element to anchor a county picker to.
//
// #2086: this used to go straight to the checkbox/radio list with the
// consequence of the choice buried in one line of body copy ("full data in
// one county, fuzzed everywhere else"). Measured on prod 2026-09-08, only 6
// of 31 profiles had ever picked a county even though 9 had the modal
// stamped as shown — the decision wasn't landing before the ask. Split into
// an "intro" screen (explain, using the same upgrade-benefits checklist
// pattern UpgradePrompt.tsx already uses) and the existing "pick" screen,
// rather than rewriting the picker UI itself. The intro also folds in two
// more #2086 asks: reframing the product as auctions-and-leads (not just
// the map/auction-tracker framing this modal predates, #875) and making the
// weekly-digest opt-in ("picking a county also turns this on") visible
// instead of implicit — see engine_digest.go's chosen_counties fallback
// (#2075), which is what actually wires a chosen county to the digest.
import React, { useEffect, useMemo, useState } from "react";
import { getCounties, putProfileCounties, markCountiesOnboarded } from "../api";
import { County } from "../types";
import { getPlanCapabilities } from "../lib/planCapabilities";
import { capture } from "../lib/analytics";
import { useFocusTrap } from "../hooks/useFocusTrap";

interface CountyPickerModalProps {
  plan: "free" | "scout";
  suggestedCounty?: string | null;
  /** The user's current chosen counties. Pre-selected so "Change county"
   *  edits the existing set instead of silently replacing it: the server
   *  accepts any strict subset of the current set without cadence checks, so
   *  saving the suggestion alone used to drop every other county quietly. */
  currentCounties?: string[];
  /** Dismissed without saving (Skip, ×, backdrop, Escape). */
  onClose: () => void;
  /** Saved successfully. The modal does NOT also call onClose: a dismiss and
   *  a save mean different things to the caller (snooze vs done). */
  onSaved: (counties: string[]) => void;
}

function planCountyNoun(max: number): string {
  return max === 1 ? "county" : "counties";
}

/** Initial selection: the user's current counties when they have any
 *  (capped to the plan), otherwise the suggestion. Exported for tests. */
export function initialCountySelection(
  currentCounties: string[] | undefined,
  suggestedCounty: string | null | undefined,
  maxCounties: number
): string[] {
  const current = (currentCounties || []).filter(Boolean);
  if (current.length > 0) return maxCounties > 0 ? current.slice(0, maxCounties) : current;
  return suggestedCounty ? [suggestedCounty] : [];
}

const CountyPickerModal: React.FC<CountyPickerModalProps> = ({
  plan,
  suggestedCounty,
  currentCounties,
  onClose,
  onSaved,
}) => {
  // Sourced from planCapabilities rather than derived here — this used to be
  // a private `plan === "free" ? 1 : 3`, which silently assumed every
  // non-free plan reaching this modal was Scout.
  const maxCounties = getPlanCapabilities(plan).countyLimit;

  // "intro" explains the choice; "pick" is the existing list UI. Starts on
  // "intro" every time the modal mounts — it's short (one click past), and
  // a returning user who hit "Skip for now" gets the explanation again
  // rather than us trying to remember they've seen it once.
  const [screen, setScreen] = useState<"intro" | "pick">("intro");

  const [counties, setCounties] = useState<County[]>([]);
  const [loadingCounties, setLoadingCounties] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [selected, setSelected] = useState<string[]>(() =>
    initialCountySelection(currentCounties, suggestedCounty, maxCounties)
  );
  const [capMessage, setCapMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const hadCounties = (currentCounties || []).length > 0;

  // Every way out that is not a save. Funnel event for the onboarding drop-off
  // (#133): before this the only signal was a stamp that fired on mount.
  const dismiss = () => {
    capture("county_skipped", { plan, screen, had_counties: hadCounties });
    onClose();
  };
  // Both screens render the same backdrop > modal skeleton, so React reuses
  // one DOM node across the intro -> pick switch and the trap stays attached.
  const trapRef = useFocusTrap<HTMLDivElement>({ onEscape: dismiss });

  // Reuse the existing county-list fetch (also used by CountySelector) —
  // don't invent a second one.
  useEffect(() => {
    getCounties()
      .then(setCounties)
      .catch(() => setCounties([]))
      .finally(() => setLoadingCounties(false));
  }, []);

  // Sorted here as well as in the API (CountyRepository.GetAll orders by
  // name) so the list is alphabetical whichever side deploys first. Filtering
  // only narrows what is shown: `selected` is untouched, so a choice made
  // before typing a new search still counts toward the cap and gets saved.
  const visibleCounties = useMemo(() => {
    const sorted = [...counties].sort((a, b) => a.name.localeCompare(b.name));
    const q = filterText.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((c) => `${c.name}, ${c.state}`.toLowerCase().includes(q));
  }, [counties, filterText]);

  // counties_onboarded_at used to be stamped HERE, on mount — so "Skip for
  // now", a stray backdrop click or Escape ended onboarding for good: the
  // picker never came back, chosen_counties stayed empty, and every fuzzed
  // card from then on opened the paid upgrade prompt instead of the free
  // county the user never got to pick. It is now stamped only after a
  // successful save (handleSubmit); a dismiss is a snooze, handled by the
  // caller.

  // Free (maxCounties === 1) renders true radio inputs below — native
  // single-select semantics mean picking a new county always just swaps
  // the selection, so there's nothing to cap or explain here. The
  // checkbox-with-cap-message path only applies to multi-select plans
  // (Scout today, Team if it ever participates).
  const selectSingle = (id: string) => {
    setErrorMessage(null);
    setSelected([id]);
  };

  const toggleCounty = (id: string) => {
    setErrorMessage(null);
    setSelected((prev) => {
      if (prev.includes(id)) {
        setCapMessage(null);
        return prev.filter((c) => c !== id);
      }
      if (prev.length >= maxCounties) {
        setCapMessage(
          `You can select up to ${maxCounties} ${planCountyNoun(maxCounties)} on the ${plan} plan. Deselect one first to swap it.`
        );
        return prev;
      }
      setCapMessage(null);
      return [...prev, id];
    });
  };

  const handleSubmit = async () => {
    if (selected.length === 0 || submitting) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const resp = await putProfileCounties(selected);
      // Best-effort: a failed stamp only means the server still reads this
      // user as not onboarded. The client already has chosen counties, and
      // the auto-open also checks those, so the picker does not come back.
      markCountiesOnboarded().catch(() => {});
      capture("county_picked", {
        plan,
        counties: resp.chosenCounties,
        count: resp.chosenCounties.length,
        used_suggestion: !!suggestedCounty && resp.chosenCounties.includes(suggestedCounty),
        changed: hadCounties,
      });
      onSaved(resp.chosenCounties);
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      if (data?.error === "too_many_counties") {
        setErrorMessage(
          data.message || `Your ${data.plan || plan} plan allows up to ${data.limit ?? maxCounties} counties.`
        );
      } else if (data?.error === "invalid_county_id") {
        setErrorMessage("One or more selected counties are no longer valid. Please refresh and try again.");
      } else if (data?.error === "plan_not_eligible_for_county_selection") {
        setErrorMessage(data.message || "Your plan doesn't require county selection.");
      } else if (status === 403 && data?.error === "county_change_not_yet_eligible") {
        const eligibleAt = data.eligible_at ? new Date(data.eligible_at) : null;
        const when = eligibleAt && !isNaN(eligibleAt.getTime()) ? eligibleAt.toLocaleDateString() : "later";
        setErrorMessage(
          `Your county selection changes every 180 days — you're not eligible to change it again until ${when}.`
        );
      } else {
        setErrorMessage("Couldn't save your counties. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const atCap = selected.length >= maxCounties;

  if (screen === "intro") {
    return (
      <div className="auth-backdrop" onClick={dismiss}>
        <div
          className="county-picker-modal"
          onClick={(e) => e.stopPropagation()}
          ref={trapRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="county-picker-title"
        >
          <button className="auth-close" onClick={dismiss} aria-label="Close">×</button>

          <h2 className="upgrade-title" id="county-picker-title">Why we ask for a county</h2>
          <p className="upgrade-reason">
            Parcely tracks every NC foreclosure auction plus the off-market lead
            pool behind it — pre-foreclosure notices, tax delinquencies, probate
            filings. Your plan shows full detail (owner info, assessed value,
            equity, bid history) only inside{" "}
            {plan === "free" ? "the one county you pick" : "the counties you pick (up to 3)"}.
            Every other county still shows up on the map and in search — that
            data is just fuzzed until you pick it or upgrade.
          </p>
          <div className="upgrade-benefits">
            <div className="upgrade-benefit">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M3 8.5l3.2 3.2L13 4" />
              </svg>
              <span>Full owner + property detail — auctions and off-market leads alike — in your county</span>
            </div>
            <div className="upgrade-benefit">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M3 8.5l3.2 3.2L13 4" />
              </svg>
              <span>Every other NC county still visible on the map, fuzzed until you pick it or upgrade</span>
            </div>
            <div className="upgrade-benefit">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M3 8.5l3.2 3.2L13 4" />
              </svg>
              <span>A weekly email digest of new auctions and leads in your chosen county — no extra signup</span>
            </div>
          </div>

          <div className="upgrade-actions">
            <button type="button" className="upgrade-btn-secondary" onClick={dismiss}>
              Skip for now
            </button>
            <button type="button" className="upgrade-btn-primary" onClick={() => setScreen("pick")}>
              Choose my county
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-backdrop" onClick={dismiss}>
      <div className="county-picker-modal" onClick={(e) => e.stopPropagation()} ref={trapRef} role="dialog" aria-modal="true" aria-labelledby="county-picker-title">
        <button className="auth-close" onClick={dismiss} aria-label="Close">×</button>

        <h2 className="upgrade-title" id="county-picker-title">Choose your county</h2>
        <p className="upgrade-reason">
          {plan === "free"
            ? "Free unlocks full detail — assessed value, equity, owner info — for auctions and off-market leads alike, in one county. Pick the one you're watching."
            : "Scout unlocks full detail — auctions and off-market leads alike — in up to 3 counties. Pick the ones you're watching."}
          {" "}You can browse every county on the map either way.
        </p>
        <p className="county-picker-note">
          Your pick is locked for 180 days, with one correction allowed in the first 48 hours. Pro has no county lock.
        </p>

        {!loadingCounties && counties.length > 0 && (
          <input
            type="search"
            className="county-picker-search"
            placeholder="Search counties…"
            aria-label="Search counties"
            autoComplete="off"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        )}

        {loadingCounties ? (
          <p className="county-picker-loading">Loading counties…</p>
        ) : counties.length === 0 ? (
          <p className="county-picker-loading">Couldn't load the county list. Please try again shortly.</p>
        ) : (
          <div className="county-picker-list" role="group" aria-label="Select your counties">
            {visibleCounties.length === 0 && (
              <p className="county-picker-loading">No counties match &ldquo;{filterText.trim()}&rdquo;.</p>
            )}
            {visibleCounties.map((c) => {
              const isChecked = selected.includes(c.id);
              const isDisabledLook = maxCounties > 1 && atCap && !isChecked;
              return (
                <label
                  key={c.id}
                  className={"county-picker-option" + (isDisabledLook ? " county-picker-option--capped" : "")}
                >
                  <input
                    type={maxCounties === 1 ? "radio" : "checkbox"}
                    name={maxCounties === 1 ? "county-picker-single" : undefined}
                    checked={isChecked}
                    onChange={() => (maxCounties === 1 ? selectSingle(c.id) : toggleCounty(c.id))}
                  />
                  <span>{c.name}, {c.state}</span>
                  {suggestedCounty === c.id && (
                    <span className="county-picker-suggested">Suggested</span>
                  )}
                </label>
              );
            })}
          </div>
        )}

        {/* #2086 item 4: make the digest opt-in visible at the moment of
            choice, not just implicit in a plan comparison somewhere else.
            Reuses .county-picker-cap-msg's look rather than a new style —
            this is copy, not a new visual treatment. Backend wiring is
            engine_digest.go's chosen_counties fallback (#2075); this is
            just naming that relationship in the UI. */}
        {!loadingCounties && counties.length > 0 && (
          <p className="county-picker-cap-msg">
            This also turns on your weekly email digest — new auctions and leads
            in {selected.length > 1 ? "these counties" : "this county"}, every Monday.
          </p>
        )}

        {capMessage && <p className="county-picker-cap-msg">{capMessage}</p>}
        {errorMessage && <p className="county-picker-error">{errorMessage}</p>}

        <div className="upgrade-actions">
          <button type="button" className="upgrade-btn-secondary" onClick={dismiss}>
            Skip for now
          </button>
          <button
            type="button"
            className="upgrade-btn-primary"
            onClick={handleSubmit}
            disabled={selected.length === 0 || submitting || loadingCounties}
          >
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CountyPickerModal;
